import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { debounce } from "lodash";
import { icon } from "../../config";
import { getUserChips, getWorkflow, getWorkflowDefaults } from "../../shared/services/workflow.services";
import { WorkflowContextProvider, useWorkflowContext } from "../Workflow/WorkflowContextProvider";
import WorkflowVariables from "../Workflow/WorkflowVariables/WorkflowVariables";
import ComponentLibrary from "./ComponentLibrary";
import UserChips from "./UserChips";
import "./styles/FormBuilderSidebar.scss";

/**
 * Form Builder Left Sidebar Component
 * Contains Smart Chips (form logic) and Properties (field settings)
 */
const FormBuilderSidebar = ({ usedWorkflowVariableIds, workflowId, projectId, formData, formId }) => {
  const { workflowDefaults, setWorkflowDefaults, setWorkflow } = useWorkflowContext();
  const [userChipsArray, setUserChipsArray] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  // Get user and project data from Redux
  const { user } = useSelector((state) => state.auth);

  // Debounce search term (300ms delay)
  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setDebouncedSearchTerm(value);
    }, 300),
    []
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSetSearchTerm(value);
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);

  // Fill workflowDefaults and workflow data if not already populated
  useEffect(() => {
    (async () => {
      if (!workflowDefaults || workflowDefaults?.length == 0) {
        let defaults = await getWorkflowDefaults({ userId: user.id, projectId: projectId, companyId: user.companyId });
        setWorkflowDefaults(defaults);
      }
      let userChipsResult = await getUserChips({ companyId: user.companyId });
      setUserChipsArray(userChipsResult || []);
      // Get workflow data
      let workflowData = workflowId && (await getWorkflow({ workflowId: workflowId, isWorkflow: true }));
      setWorkflow(workflowData?.workflowBasicDetails);
    })();
  }, [workflowDefaults, user.id, user.companyId, setWorkflowDefaults, workflowId, setWorkflow]);

  // const generateColumns = (columnCount) => {
  //   let selectedColumns = selectedElement?.properties?.columns;
  //   if (selectedColumns?.length > columnCount) {
  //     return selectedColumns?.slice(0, columnCount);
  //   } else if (selectedColumns?.length < columnCount) {
  //     return [
  //       ...selectedColumns,
  //       ...Array.from({ length: columnCount - selectedColumns?.length }, (_, i) => ({
  //         id: selectedColumns?.length + i,
  //         field: `column${selectedColumns?.length + i}`,
  //         title: `Column ${selectedColumns?.length + i}`,
  //       })),
  //     ];
  //   } else {
  //     return selectedColumns;
  //   }
  // };

  return (
    <>
      <aside className='form-builder-sidebar d-flex flex-column'>
        {/* Top-level Search Bar */}
        <div className='sidebar-section border-bottom pb-2 mb-2'>
          <div className='d-flex align-items-center position-relative'>
            <span className='search-icon position-absolute'>{icon.SEARCH}</span>
            <Input type='text' value={searchTerm} onChange={handleSearchChange} placeholder='Search' className='search-input' />
          </div>
        </div>
        <div className='form-builder-sidebar overflow-y-auto sidebar-height'>
          <div className='sidebar-section border-bottom pb-2 pt-1 mb-1'>
            <WorkflowVariables
              isFormVariable={true}
              usedWorkflowVariableIds={usedWorkflowVariableIds}
              workflowId={workflowId}
              projectId={projectId}
              searchTerm={debouncedSearchTerm}
              formData={formData}
              formId={formId}
            />
          </div>
          <div className='sidebar-section border-bottom pb-2 mb-1'>
            <ComponentLibrary searchTerm={debouncedSearchTerm} />
          </div>
          <div className='sidebar-section border-bottom pb-2 mb-1'>
            <UserChips userChipsArray={userChipsArray} searchTerm={debouncedSearchTerm} />
          </div>
        </div>
      </aside>
    </>
  );
};

export default FormBuilderSidebar;
